import { Core } from "./core.js";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SplitType from "split-type";
import Lenis from "lenis";
import 'lenis/dist/lenis.css';

let instance;

class Animations {

  /**
   * Store scroll lib instance for usage
   */
  scroller = false;

  constructor() {
    if (instance) {
      throw new Error("You can only create one instance!");
    }
    instance = this;
    if ( Core.isAdmin() ) return;

    gsap.registerPlugin(ScrollTrigger);

    this.setSmoothScroll();

    this.animateToggleHeading();
    this.animatePrimaryButtonHover();
    setTimeout(() => {
      window?.MktoForms2?.whenRendered(this.animatePrimaryButtonHover);
    }, 500);
    // TODO: add an observer to update dynamically generated button from forms...

    this.heroAnimObserver();
    this.animateCardIconsOnDesktop();
    this.animateFeatureTextMedia();
    setInterval( this.animateHeroMockup, 2500 );

    return instance;
  }

  getInstance() {
    return this;
  }

  setSmoothScroll() {
    // Initialize a new Lenis instance for smooth scrolling
    const lenis = new Lenis({
      prevent: (node) => {
        if ( (
          node.classList.nodeName === 'ACCESS-WIDGET-UI' ) || // Accessibility widget
          node.classList.contains('cmpwrapper') // CMP / Cookies
        ) {
          return true;
        }
      }
    });

    this.scroller = lenis;
    window.scroller = lenis;

    // Synchronize Lenis scrolling with GSAP's ScrollTrigger plugin
    lenis.on('scroll', ScrollTrigger.update);

    // Add Lenis's requestAnimationFrame (raf) method to GSAP's ticker
    // This ensures Lenis's smooth scroll animation updates on each GSAP tick
    gsap.ticker.add((time) => {
      lenis.raf(time * 1000); // Convert time from seconds to milliseconds
    });

    // Disable lag smoothing in GSAP to prevent any delay in scroll animations
    gsap.ticker.lagSmoothing(0);
  }

  animateTextFillOnScroll( textSelector = '.wysiwyg-section-wysiwyg > h3' ) {

    // console.log('✨', 'start Animate Wysiwyg H3 anim'); // eslint-disable-line

    const splitTypes = document.querySelectorAll(textSelector)
    splitTypes.forEach((text) => {

        const bg = 'var(--color-grey)';
        const fg = 'var(--color-secondary)';

        const splittedText = new SplitType(text, { types: 'words'})

        gsap.fromTo(splittedText.words,
            {
                color: bg,
            },
            {
                color: fg,
                duration: 0.3,
                stagger: 0.02,
                scrollTrigger: {
                    trigger: text,
                    start: 'top 80%',
                    end: 'top 20%',
                    scrub: true,
                    markers: false,
                    toggleActions: 'play play reverse reverse'
                }
        })
    })
  }

  animateToggleHeading() {
    const els = document.querySelectorAll('.toggle-animation');
    if (!els.length) return;

    // Disable anim on mobile for now
    if (Core.isBpLessThan('xl')) {
      els.forEach(el => { el.classList.add('active'); });
      return;
    }

    // Observer for adding active class when element is in the middle of the screen
    const activateObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Add active class when element is visible in the middle
          entry.target.classList.add('active');

          // Disable afterwards
          entry.target.addEventListener('animationend', () => {
            entry.target.classList.add('done');
            observer.unobserve(entry.target);
          });
        }
      });
    }, {
      // Configure to trigger when element is in the middle of the screen
      root: null, // Use the viewport as root
      rootMargin: '-20% 0px -20% 0px', // Only trigger when element is in middle 10% of screen
      threshold: 0 // Trigger as soon as any part is visible in the target area
    });

    // Observer for removing active class only when element is completely offscreen
    // const deactivateObserver = new IntersectionObserver((entries, observer) => {
    //   entries.forEach(entry => {
    //     if (!entry.isIntersecting && entry.target.classList.contains('active')) {
    //       // Remove active class only when element is completely out of viewport
    //       entry.target.classList.remove('active');

    //       // Disable afterwards
    //       observer.unobserve(entry.target);
    //     }
    //   });
    // }, {
    //   // Configure to detect when element is completely out of viewport
    //   root: null, // Use the viewport as root
    //   rootMargin: '0px 0px 0px 0px', // No margin - detect actual viewport boundaries
    //   threshold: 0 // Trigger when element is completely out
    // });

    // Observe all toggle animation elements with both observers
    els.forEach(el => {
      activateObserver.observe(el);
      // deactivateObserver.observe(el);
    });
  }

  animatePrimaryButtonHover() {
    const buttons = [
      ...document.querySelectorAll('.btn-primary'),
      ...document.querySelectorAll('.mktoButton'),
      ...document.querySelectorAll('.btn-menu-2')
    ];
    if (!buttons.length) return;

    buttons.forEach(button => {
      // Skip if already processed
      if (button.dataset.hoverProcessed) return;
      button.dataset.hoverProcessed = 'true';

      // Create colorful layers container
      const layersContainer = document.createElement('div');
      layersContainer.className = 'btn-hover-layers';
      layersContainer.style = 'border-radius: inherit;';

      // Create multiple colored layers
      const colors = [
        'var(--color-orange)',
        'var(--color-blue-3)',
        'var(--color-blue-2)',
      ];

      colors.forEach((color, index) => {
        const layer = document.createElement('div');
        layer.className = `btn-hover-layer layer-${index + 1}`;
        layer.style.backgroundColor = color;
        layer.style.position = 'absolute';
        layer.style.top = '0';
        layer.style.left = '0';
        layer.style.width = '100%';
        layer.style.height = '100%';
        layer.style.borderRadius = 'inherit';
        // layer.style.opacity = '0';
        layer.style.zIndex = '-1';
        layer.style.transform = `translateY(0)`;
        layer.style.transition = 'all 0.4s var(--easing, cubic-bezier(0.25, 0.46, 0.45, 0.94))';

        layersContainer.appendChild(layer);
      });

      // Make button relative and add layers
      button.style.position = 'relative';
      button.appendChild(layersContainer);

      const btnBgDiv = document.createElement('div');
      btnBgDiv.className = 'btn-background';
      btnBgDiv.style.backgroundColor = 'inherit';
      btnBgDiv.style.position = 'absolute';
      btnBgDiv.style.inset = '-1px';
      btnBgDiv.style.borderRadius = 'inherit';
      btnBgDiv.style.zIndex = '-1';
      button.appendChild(btnBgDiv);

      // Add hover event listeners
      const layers = button.querySelectorAll('.btn-hover-layer');
      button.addEventListener('mouseenter', () => {
        let customIndex = 1;
        for (let index = layers.length; index !== 0; index--) {
          const layer = layers[index - 1];
          const translateVal = 4 + (customIndex * 2);
          // setTimeout(() => {
            layer.style.transform = `translateY(${translateVal}px)`;
          // }, customIndex * 50); // Stagger the animation
          customIndex++;
        }
      });

      button.addEventListener('mouseleave', () => {
        for (let index = layers.length; index !== 0; index--) {
          const layer = layers[index - 1];
          // setTimeout(() => {
            layer.style.transform = `translateY(0)`;
          // }, index * 30); // Faster reverse animation
        }
      });
    });
  }

  animateCardIconsOnDesktop() {

    if ( Core.isBpLessThan('lg') ) {
      return;
    }

    const cardIcons = document.querySelectorAll('.card-icon');
    if (!cardIcons.length) return;

    // Group card icons by their container to enable stagger within groups
    const iconGroups = {};

    cardIcons.forEach((icon, index) => {
      // Try to find a parent container (like .cards-container, .grid, etc.)
      const container = icon.closest('[class*="grid"], [class*="cards"], [class*="container"], section, .row') || document.body;
      const containerId = container.className || `group-${index}`;

      if (!iconGroups[containerId]) {
        iconGroups[containerId] = [];
      }
      iconGroups[containerId].push(icon);
    });

    // Animate each group
    Object.values(iconGroups).forEach(icons => {
      // Set initial state - invisible and moved down
      gsap.set(icons, {
        opacity: 0,
        y: 30,
        scale: 0.9
      });

      // Animate when in view
      gsap.to(icons, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.6,
        stagger: 0.15, // 150ms delay between each icon
        ease: "power2.out",
        scrollTrigger: {
          trigger: icons[0], // Use first icon as trigger
          start: 'top 85%',   // Start animation when top of icon is 85% down the viewport
          end: 'top 20%',     // End when top is 20% down
          toggleActions: 'play none none reverse', // Play on enter, reverse on leave
          markers: false // Set to true for debugging
        }
      });
    });

    // console.log(`🎭 Initialized card-icon animations for ${cardIcons.length} icons`);
  }

  heroAnimObserver() {
    const headerSection = document.querySelector('section.header');
    if (!headerSection) return;

    // Observer to hide/show header section based on viewport proximity
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Show when in or near viewport
          entry.target.style.visibility = 'visible';
        } else {
          // Hide when far from viewport
          entry.target.style.visibility = 'hidden';
        }
      });
    }, {
      root: null, // Use viewport as root
      rootMargin: '100px 0px 100px 0px', // Extend detection area by 100px above and below
      threshold: 0 // Trigger as soon as any part enters the extended area
    });

    observer.observe(headerSection);
  }

  animateFeatureTextMedia() {
    const sections = document.querySelectorAll('.features-text-media');
    if (!sections.length) return;

    sections.forEach(section => {
      const contentElements = section.querySelectorAll('.features-text-media-content > *');
      const wrapper = section.querySelector('.features-text-media-wrapper');
      const imageElement = wrapper.querySelector(':scope > figure > picture > img');

      // Helper to initialize animation for this section (re-queries live DOM nodes)
      const startAnimation = () => {
        // Avoid double-initialization
        if (section.dataset.ftmAnimated) return;

        const svgGroupElements = wrapper.querySelectorAll(':scope figure svg > g');
        const imageElement = wrapper.querySelector(':scope > figure');

        // nothing to animate
        if (!contentElements.length && !imageElement && (!svgGroupElements || !svgGroupElements.length)) return;

        section.dataset.ftmAnimated = 'true';

        // Create timeline
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: section,
            start: 'top 70%',
            end: 'bottom 30%',
            toggleActions: 'play none none reverse',
            markers: false
          }
        });

        // Set initial states
        if (contentElements.length) {
          gsap.set(contentElements, {
            opacity: 0,
            // x: -30,
            y: 10
          });
        }

        // If there are SVG groups, animate them (preferred). Otherwise fall back to animating an <img>.
        if (svgGroupElements && svgGroupElements.length) {
          gsap.set(svgGroupElements, {
            opacity: 0,
            y: -30,
            // scale: 1.1,
          });
        } else if (imageElement) {
          gsap.set(imageElement, {
            opacity: 0,
            scale: 1.1
          });
        }

        // Animate content elements with stagger (left to right fade)
        if (contentElements.length) {
          tl.to(contentElements, {
            opacity: 1,
            // x: 0,
            y: 0,
            duration: 0.8,
            stagger: 0.1,
            ease: "power2.out"
          }, 0);
        }

        // Animate SVG groups with stagger (preferred) or animate image fallback
        if (svgGroupElements && svgGroupElements.length) {
          tl.to(svgGroupElements, {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 1,
            stagger: 0.08,
            ease: "power2.out"
          }, 0.25); // Start slightly after content
        } else if (imageElement) {
          tl.to(imageElement, {
            opacity: 1,
            scale: 1,
            duration: 1,
            ease: "power2.out"
          }, 0.3); // Start slightly after content
        }
      };

      // If the wrapper contains an <img> that points to a .svg, wait for it to be replaced by an inline <svg>
      if (imageElement && imageElement.tagName === 'IMG' && /\.svg(\?.*)?$/i.test(imageElement.src)) {
        // If an svg is already present, just start
        const existingSvg = wrapper.querySelector(':scope figure svg');
        if (existingSvg) {
          startAnimation();
          return;
        }

        // Observe wrapper for child changes (replacement of img by svg)
        const mo = new MutationObserver((mutations, obs) => {
          const svgNow = wrapper.querySelector(':scope figure svg');
          if (svgNow) {
            obs.disconnect();
            startAnimation();
          }
        });
        mo.observe(wrapper, { childList: true, subtree: true });

        // Fallback: if replacement doesn't happen within timeout, proceed anyway
        setTimeout(() => {
          try {
            mo.disconnect();
          } catch (e) {
            // ignore
          }
          startAnimation();
        }, 1500);
      } else {
        // No SVG-replacement expected — start immediately
        startAnimation();
      }
    });

  }

  animateHeroMockup() {
    const heroImgAnimsEls = [...document.querySelectorAll('.header.content-center .header-img-mockup .has-anim-2 .img-anim')];
    if ( !heroImgAnimsEls.length ) return;

    heroImgAnimsEls.map(el => {
      el.classList.contains('hide') ?
        el.classList.replace('hide', 'show') :
        el.classList.replace('show', 'hide');
    });
  }
}

const singleton = Object.freeze(new Animations());
export default singleton;
